var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import './OneTrust.types';
const OneTrustContext = createContext(undefined);
export const OneTrustProvider = ({ children, lcpTimeout = 5000, // Default 5 seconds for LCP timeout
idleTimeout = 2000, // Default 2 seconds for requestIdleCallback timeout
 }) => {
    const [isOneTrustReady, setIsOneTrustReady] = useState(false);
    const [showBanner, setShowBanner] = useState(true);
    const [isAlertClosed, setIsAlertClosed] = useState(true);
    const [isClient, setIsClient] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    // Handle client-side mounting
    useEffect(() => {
        setIsClient(true);
    }, []);
    const acceptAll = () => {
        if (window.OneTrust) {
            window.customBannerTriggeredConsent = true;
            window.OneTrust.AllowAll();
        }
        setShowBanner(false);
        setIsAlertClosed(true);
    };
    const rejectAll = () => {
        if (window.OneTrust) {
            window.customBannerTriggeredConsent = true;
            window.OneTrust.RejectAll();
        }
        setShowBanner(false);
        setIsAlertClosed(true);
    };
    const managePreferences = () => {
        if (window.OneTrust && typeof window.OneTrust.ToggleInfoDisplay === 'function') {
            window.OneTrust.ToggleInfoDisplay();
        }
    };
    const closeBanner = () => {
        setShowBanner(false);
        setIsAlertClosed(true);
    };
    useEffect(() => {
        const handleOneTrustReady = () => {
            setIsOneTrustReady(true);
        };
        const handleConsentApplied = () => {
            setShowBanner(false);
            setIsAlertClosed(true);
        };
        window.addEventListener('oneTrustReady', handleOneTrustReady);
        window.addEventListener('OTConsentApplied', handleConsentApplied);
        return () => {
            window.removeEventListener('oneTrustReady', handleOneTrustReady);
            window.removeEventListener('OTConsentApplied', handleConsentApplied);
        };
    }, []);
    // Check OneTrust alert status on client side
    useEffect(() => {
        if (!isClient)
            return;
        const checkAlertStatus = () => {
            if (window.OneTrust && typeof window.OneTrust.IsAlertBoxClosed === 'function') {
                const alertClosed = window.OneTrust.IsAlertBoxClosed();
                setIsAlertClosed(alertClosed);
                setShowBanner(!alertClosed);
                setIsOneTrustReady(true);
            }
        };
        // Check immediately if OneTrust is already loaded
        checkAlertStatus();
        // Listen for OneTrust ready event
        const handleOneTrustReady = () => {
            checkAlertStatus();
        };
        window.addEventListener('oneTrustReady', handleOneTrustReady);
        return () => {
            window.removeEventListener('oneTrustReady', handleOneTrustReady);
        };
    }, [isClient]);
    const loadOneTrustScripts = useCallback((scriptUrl, domainScriptUrl) => __awaiter(void 0, void 0, void 0, function* () {
        if (isLoaded)
            return;
        try {
            // Load the main OneTrust script
            const script = document.createElement('script');
            script.src = scriptUrl;
            script.type = 'text/javascript';
            script.setAttribute('data-domain-script', domainScriptUrl);
            script.async = true;
            script.defer = true;
            // Wait for script to load
            yield new Promise((resolve, reject) => {
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('Failed to load OneTrust script'));
                document.head.appendChild(script);
            });
            setIsLoaded(true);
            window.OptanonWrapper = function () {
                if (window.OneTrust) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ;
                    window.customBannerActive = true;
                    window.dispatchEvent(new CustomEvent('oneTrustReady'));
                }
            };
            // Check if OneTrust is ready immediately after script load
            const checkOneTrustReady = () => {
                if (window.OneTrust) {
                    const alertClosed = window.OneTrust.IsAlertBoxClosed();
                    setIsAlertClosed(alertClosed);
                    setShowBanner(!alertClosed);
                    setIsOneTrustReady(true);
                }
                else {
                    // Only retry a few times, then give up
                    setTimeout(checkOneTrustReady, 100);
                }
            };
            checkOneTrustReady();
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Failed to load OneTrust:', error);
        }
    }), [isLoaded]);
    const initializeAfterLCP = useCallback((scriptUrl, domainScriptUrl) => {
        if (!isClient || !scriptUrl || !domainScriptUrl)
            return;
        let hasInitialized = false;
        const initialize = () => {
            if (hasInitialized)
                return;
            hasInitialized = true;
            // Use requestIdleCallback for better performance with configurable timeout
            if (window.requestIdleCallback) {
                window.requestIdleCallback(() => loadOneTrustScripts(scriptUrl, domainScriptUrl), {
                    timeout: idleTimeout,
                });
            }
            else {
                // Fallback for browsers without requestIdleCallback
                setTimeout(() => loadOneTrustScripts(scriptUrl, domainScriptUrl), 100);
            }
        };
        const initializeAfterLoad = () => {
            // Try to observe LCP
            try {
                // eslint-disable-next-line compat/compat -- Edge 16 is from 2017 and no longer supported
                const observer = new PerformanceObserver(list => {
                    const entries = list.getEntries();
                    const lcpEntry = entries[entries.length - 1];
                    if (lcpEntry) {
                        initialize();
                        observer.disconnect();
                    }
                });
                observer.observe({ entryTypes: ['largest-contentful-paint'] });
                // Fallback timeout with configurable duration
                setTimeout(() => {
                    initialize();
                    observer.disconnect();
                }, lcpTimeout);
            }
            catch (e) {
                // Fallback if LCP observation fails
                initialize();
            }
        };
        if (document.readyState === 'complete') {
            initializeAfterLoad();
            return; // Add return statement
        }
        else {
            const handleLoad = () => {
                initializeAfterLoad();
                window.removeEventListener('load', handleLoad);
            };
            window.addEventListener('load', handleLoad);
            return () => window.removeEventListener('load', handleLoad);
        }
    }, [isClient, loadOneTrustScripts, lcpTimeout, idleTimeout]);
    const contextValue = {
        isOneTrustReady,
        showBanner,
        isAlertClosed,
        isClient,
        isLoaded,
        initializeAfterLCP,
        acceptAll,
        rejectAll,
        managePreferences,
        closeBanner,
    };
    return _jsx(OneTrustContext.Provider, { value: contextValue, children: children });
};
export const useOneTrustContext = () => {
    const context = useContext(OneTrustContext);
    if (context === undefined) {
        throw new Error('useOneTrustContext must be used within a OneTrustProvider');
    }
    return context;
};
